import React, { useState } from "react";
import ShowNext from "./ShowNext";
import OtpForm from "./OtpForm";
import axios from "axios";
import "./../UserSignup.css";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const SIGNUP_PATH = process.env.REACT_APP_SIGNUP_PATH || "/usersignup";
const OTP_SEND_PATH = process.env.REACT_APP_SIGNUP_OTP_SEND_PATH || "/send-email-otp";
const OTP_VERIFY_PATH =
  process.env.REACT_APP_SIGNUP_OTP_VERIFY_PATH || "/verify-email-otp";
const IS_LOCAL_PAGE = window.location.hostname === "localhost";
const FALLBACK_LOCAL_BACKEND_URL = IS_LOCAL_PAGE ? "http://localhost:8000" : "";
const normalizePath = (path = "") => {
  if (!path) return "/";
  return path.startsWith("/") ? path : `/${path}`;
};

const withApiPrefix = (path) =>
  path.startsWith("/api/") || path === "/api" ? path : `/api${path}`;

const withoutApiPrefix = (path) =>
  path.startsWith("/api/") ? path.slice(4) : path === "/api" ? "/" : path;

const joinUrl = (baseUrl, path) => {
  const normalizedPath = normalizePath(path);
  if (baseUrl.endsWith("/api") && normalizedPath.startsWith("/api/")) {
    return `${baseUrl}${normalizedPath.slice(4)}`;
  }
  return `${baseUrl}${normalizedPath}`;
};

const getCandidatePaths = (path) => {
  const normalizedPath = normalizePath(path);
  const withAuthPrefix = normalizedPath.startsWith("/auth/")
    ? normalizedPath
    : `/auth${normalizedPath}`;
  const withApiAuthPrefix = withApiPrefix(withAuthPrefix);

  return Array.from(
    new Set([
      normalizedPath,
      withApiPrefix(normalizedPath),
      withoutApiPrefix(normalizedPath),
      withAuthPrefix,
      withApiAuthPrefix,
      withoutApiPrefix(withAuthPrefix),
    ])
  );
};

const getBaseUrls = () => {
  const ordered = IS_LOCAL_PAGE
    ? [FALLBACK_LOCAL_BACKEND_URL, BACKEND_URL]
    : [BACKEND_URL, FALLBACK_LOCAL_BACKEND_URL];

  return Array.from(new Set(ordered.filter(Boolean)));
};

const postWith404Fallback = async (path, payload, options = {}) => {
  const { retryOnOtpPrecondition = false } = options;
  const candidates = getCandidatePaths(path);
  const baseUrls = getBaseUrls();
  let lastError;

  for (const baseUrl of baseUrls) {
    for (const candidate of candidates) {
      try {
        return await axios.post(joinUrl(baseUrl, candidate), payload);
      } catch (err) {
        const status = err?.response?.status;
        const message = err?.response?.data?.message;

        // On Android/native WebView, localhost requests can fail with no HTTP status.
        // Continue trying other base URLs/endpoints before failing.
        if (!status) {
          lastError = err;
          continue;
        }

        if (
          retryOnOtpPrecondition &&
          status === 400 &&
          message === "Please verify your email OTP before signup" &&
          baseUrls.length > 1
        ) {
          lastError = err;
          continue;
        }

        if (status !== 404) {
          throw err;
        }
        lastError = err;
      }
    }
  }

  throw lastError || new Error("OTP API endpoint not found");
};

const Addhar = () => {
  const navigate = useNavigate();
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  const [userDetail, setUserDetail] = useState({
    full_Name: "",
    contact: "",
    email: "",
    password: "",
    confirm_password: "",
  });

  function changeHandler(e) {
    setUserDetail({ ...userDetail, [e.target.name]: e.target.value });
  }

  function addfocus(e) {
    e.target.closest(".form__div").classList.add("focus");
  }

  function remfocus(e) {
    if (e.target.value === "") {
      e.target.closest(".form__div").classList.remove("focus");
    }
  }

  const generateOtp = async (e) => {
    e.preventDefault();
    if (!userDetail.full_Name || !userDetail.email || !userDetail.contact) {
      return toast.error("Please fill full name, email and contact.");
    }

    if (userDetail.password !== userDetail.confirm_password) {
      return toast.error("Password and Confirm Password do not match.");
    }

    setLoading(true);
    try {
      const res = await postWith404Fallback(
        `${SIGNUP_PATH}${OTP_SEND_PATH}`,
        { email: userDetail.email }
      );

      toast.success(res.data.message || "OTP sent to your email.");
      setOtpSent(true);
      setResendTimer(59);
    } catch (err) {
      const status = err?.response?.status;
      toast.error(
        err.response?.data?.Message ||
          err.response?.data?.message ||
          (status === 404
            ? "Signup OTP API not found on server."
            : status
            ? `OTP request failed (${status}).`
            : null) ||
          "Error while generating OTP."
      );
    } finally {
      setLoading(false);
    }
  };

  const VerifyOtp = async (e, userOtp) => {
    e.preventDefault();
    if (!userOtp) {
      return toast.error("Please enter OTP.");
    }
    if (userDetail.password !== userDetail.confirm_password) {
      return toast.error("Password and Confirm Password do not match.");
    }

    setLoading(true);
    try {
      await postWith404Fallback(`${SIGNUP_PATH}${OTP_VERIFY_PATH}`, {
        email: userDetail.email,
        otp: userOtp,
      });

      const signupRes = await postWith404Fallback(
        SIGNUP_PATH,
        { userDetail },
        { retryOnOtpPrecondition: true }
      );
      toast.success(signupRes.data.message || "User registered successfully");
      navigate("/user_login");
    } catch (err) {
      const status = err?.response?.status;
      toast.error(
        err.response?.data?.Message ||
          err.response?.data?.message ||
          (status === 404
            ? "Signup OTP API not found on server."
            : status
            ? `Request failed (${status}).`
            : null) ||
          "Something went wrong during verification."
      );
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    let timer;
    if (resendTimer > 0) {
      timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendTimer]);

  return (
    <form className="form__content">
      <h1 className="form__title">Welcome</h1>

      {["full_Name", "email", "contact", "password", "confirm_password"].map(
        (field, index) => (
          <div className="form__div form__div-one" key={index}>
            <div className="form__icon">
              <i
                className={`bx ${
                  field.includes("password")
                    ? "bx-lock"
                    : field === "email"
                    ? "bx-envelope"
                    : field === "contact"
                    ? "bx-phone"
                    : "bx-user-circle"
                }`}
              ></i>
            </div>
            <div className="form__div-input">
              <label className="form__label">
                {field.replace("_", " ").toUpperCase()}
              </label>
              <input
                onChange={changeHandler}
                onFocus={addfocus}
                onBlur={remfocus}
                required
                id={field}
                type={
                  field.includes("password")
                    ? "password"
                    : field === "email"
                    ? "email"
                    : "text"
                }
                name={field}
                value={userDetail[field]}
                className="form__input"
                disabled={loading}
              />
            </div>
          </div>
        )
      )}

      {otpSent ? (
        <OtpForm
          VerifyOtp={VerifyOtp}
          loading={loading}
          resendTimer={resendTimer}
          resendOtp={generateOtp}
        />
      ) : (
        <ShowNext generateOtp={generateOtp} loading={loading} />
      )}
    </form>
  );
};

export default Addhar;
